import React, { useCallback, useState } from 'react'
import { Form, Formik } from 'formik'
import { useFormContext } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation, useSearchParams } from 'react-router-dom'
import { concatMap, from, of, switchMap } from 'rxjs'

import { Button, Typography } from '@mui/material'
import { AlertMessage } from '@procom-labs/atoms'
import { RecaptchaActions, useSubscriptionRef } from '@procom-labs/common'
import { DigitalCodeInput, useAlert } from '@procom-labs/molecules'

import { useAuthService, useRecaptcha } from '@auth-portal/hooks'
import { UserExistResponse } from '@auth-portal/types'

interface FormValues {
  verificationCode: string
}

const defaultValues: FormValues = {
  verificationCode: '',
}

const MAX_LOGIN_ATTEMPTS = 1

export const VerifyOtpLogin: React.FC<{isUserEmailVerified: boolean}> = ({ isUserEmailVerified }) => {
  const { addAlert } = useAlert()
  const { t } = useTranslation('main')
  const [searchParams] = useSearchParams()
  const { getRecaptchaToken } = useRecaptcha()
  const authService = useAuthService()

  const location = useLocation()
  const userResponse = location.state as UserExistResponse

  const handleSignInSubscriptionRef = useSubscriptionRef()
  const handleVerifyCodeSubscriptionRef = useSubscriptionRef()

  const email = searchParams.get('email')
  const isNewUser = (searchParams.get('isNewUser') ?? '') === 'true'
  const firstLogin = (searchParams.get('firstLogin') ?? '') === 'true'

  const [otp, setOtp] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)

  const { setValue } = useFormContext()

  const handleGenerateOtp = useCallback(
    (code: string) => {
      if (!email) return

      const {
        onboardingParam,
        migrationToken,
        isFboMigrationTokenValid,
        tenantParam,
      } = userResponse
      const isFboUser = migrationToken && isFboMigrationTokenValid
      const isOnboardingInvite = onboardingParam

      handleSignInSubscriptionRef.current = from(
        getRecaptchaToken(RecaptchaActions.CodeSignIn)
      )
        .pipe(
          switchMap((token) =>
            authService.handleOtpSignIn(code, email, token).pipe(
              concatMap(() =>
                from(getRecaptchaToken(RecaptchaActions.ActivateFboMapping))
              ),
              concatMap((fboMappingToken) => {
                return isFboUser || isOnboardingInvite
                  ? authService.activateFboUserMapping(
                      migrationToken,
                      onboardingParam,
                      email,
                      fboMappingToken,
                      tenantParam
                    )
                  : of(null)
              })
            )
          )
        )
        .subscribe({
          next: () => {
            authService.redirectToClientPortal({
              isOtpLogin: true,
              ...(isNewUser && { newAccount: 'true' }),
              ...(firstLogin && { firstLogin: 'true' }),
            })
          },
        })
    },
    [
      getRecaptchaToken,
      email,
      isNewUser,
      firstLogin,
      userResponse,
      handleSignInSubscriptionRef,
    ]
  )

  const handleVerifyCode = useCallback(
    (code: string) => {
      if (!email) return
      setIsVerifying(true)
      setOtp(code)
      setValue('otp', code)
      handleVerifyCodeSubscriptionRef.current = from(
        getRecaptchaToken(RecaptchaActions.RequestResetPassword)
      ).subscribe({
        next: (token) => {
          authService.validateOtpSignIn(email, code, token).subscribe({
            next: (response) => {
              if (response.hasCodeExpired) {
                addAlert({
                  severity: 'error',
                  message: t('form.alerts.codeExpired'),
                })
              } else if (response.hasReachedMaxAttempts) {
                addAlert({
                  severity: 'error',
                  message: t('form.alerts.maxAttempts', {
                    hour: MAX_LOGIN_ATTEMPTS,
                  }),
                })
              } else if (response.isCodeValid) {
                handleGenerateOtp(code)
              } else {
                addAlert({
                  severity: 'error',
                  message: t('form.alerts.invalidCode'),
                })
              }
              setIsVerifying(false)
            },
            error: (error) => {
              setIsVerifying(false)
              addAlert({
                severity: 'error',
                message: error.message,
              })
            },
          })
        },
      })
    },
    [
      t,
      email,
      addAlert,
      getRecaptchaToken,
      handleGenerateOtp,
      setOtp,
      setValue,
      handleVerifyCodeSubscriptionRef,
    ]
  )

  const handleSubmit = useCallback(() => {
    handleVerifyCode(otp)
  }, [otp, handleVerifyCode])

  return (
    <>
      <Typography
        component="h1"
        variant="h30Bold800"
        align="center"
        lineHeight={1.5}
        mb={1}
      >
        {t('organisms.mfaLoginCodeVerification.heading')}
      </Typography>

      <AlertMessage
        severity="info"
        sx={{ marginBlockEnd: 4, textAlign: 'left' }}
      >
        {isUserEmailVerified ? (
          <Typography>
            <Trans i18nKey="common.alert.verifyDigitalCode" values={{ email }} />
          </Typography>
        ) : (
          <Typography>
            <Trans i18nKey="common.alert.verifyDigitalCodeEmailNotVerified" values={{ email }} />
          </Typography>
        )}
      </AlertMessage>

      <Typography sx={{ textAlign: 'center', mb: 2 }}>
        {t('form.createAccount.headings.enterCodeToVerify')}
      </Typography>

      <Formik initialValues={defaultValues} onSubmit={handleSubmit}>
        {() => (
          <Form className="form-root">
            <DigitalCodeInput
              isVerifying={isVerifying}
              handleVerifyCode={handleVerifyCode}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{ marginBlockEnd: 2, marginTop: 4 }}
              id="cta-mfa-verify-code"
            >
              {t('common.btn.submit')}
            </Button>
          </Form>
        )}
      </Formik>
    </>
  )
}
